import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { messages } from '@sorare/core/src/components/seo/Header';
import {
  FOOTBALL_PLAY_RIVALS,
  FOOTBALL_PLAY_RIVALS_DRAFT_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useEvents from '@sorare/core/src/lib/events/useEvents';

import { GameTeamsMatchup } from '@sorare/gameplay/src/components/game/Game/GameTeamsMatchup';

import {
  UpcomingGame_game,
  UpcomingGame_player,
} from './__generated__/index.graphql';

interface Props {
  player: UpcomingGame_player;
  game: UpcomingGame_game;
}

const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  align-items: center;
`;

const StyledButton = styled(Button)`
  width: fit-content;
`;

export const UpcomingGame = ({ player, game }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { signUp } = useConnectionContext();
  const track = useEvents();
  const { homeTeam, awayTeam, rivalsGame } = game;

  if (!homeTeam || !awayTeam) return null;

  const startPlayingProps = currentUser
    ? {
        to: rivalsGame
          ? generatePath(FOOTBALL_PLAY_RIVALS_DRAFT_SLUG, {
              slug: rivalsGame.slug,
            })
          : generatePath(FOOTBALL_PLAY_RIVALS),
      }
    : {
        onClick: () => {
          track('Click Sign Up', { variation: `Play with player` });
          signUp();
        },
      };

  return (
    <Wrapper>
      <GameTeamsMatchup game={game} displayDay />
      <StyledButton {...startPlayingProps} size="medium">
        <FormattedMessage
          {...messages.startPlayingWith}
          values={{
            firstName: player.matchName,
          }}
        />
      </StyledButton>
    </Wrapper>
  );
};

UpcomingGame.fragments = {
  game: gql`
    fragment UpcomingGame_game on Game {
      id
      homeTeam {
        slug
        code
        ...TeamAvatar_team
      }
      awayTeam {
        slug
        code
        ...TeamAvatar_team
      }
      rivalsGame {
        slug
      }
      ...GameTeamsMatchup_anyGameInterface
    }
    ${GameTeamsMatchup.fragments.anyGameInterface}
  ` as TypedDocumentNode<UpcomingGame_game>,
  player: gql`
    fragment UpcomingGame_player on Player {
      slug
      matchName
    }
  ` as TypedDocumentNode<UpcomingGame_player>,
};
